import { useEffect, useMemo, useState } from 'react';
import { linkSelection, unlink } from '../domain/resources';
import { coreSync } from '../lib/core';
import { isActive } from '../domain/ledger';
import {
  attendanceComputedMinor, attendancePaymentReadBack, attendanceDisclosure,
  attendanceFieldErrors, attendanceUsesPackage, attendanceSettlement,
  attendanceConsequence, attendanceDescription, clampNote,
  CLOSED_NOTICE, toggledNoticeChannel, chosenNotices as noticesChosen,
  remindedChannels as channelsReminded, noticeAlreadyQueued as alreadyQueued,
} from '../domain/lessonAttendance';
import { useCurrency, useLocale, useMoneyFormat } from '../context/LocaleContext';
import { useAttendance } from '../data/useAttendance';
import { useLedger } from '../data/useLedger';
import { usePackages } from '../data/usePackages';
import { useMakeup } from '../data/useMakeup';
import { useCategories } from '../data/useCategories';
import { allowsMakeupCredits } from '../domain/categories';
import { useFamilies } from '../data/useFamilies';
import { useNotifications } from '../data/useNotifications';
import { useAuth } from '../context/AuthContext';
import { useSmsAvailable } from '../context/SmsGateContext';
import { smsGatedChoice } from '../domain/smsGate';
import {
  CHANNELS, familyRecipients, myselfRecipient, notesRecipients,
  attendanceMessageRequests, reminderAlreadySentText,
  ATTENDANCE_SENDS, ATTENDANCE_SEND_STATES, attendanceSendState, attendanceSendWording,
} from '../domain/notifications';

// Taking attendance (SPEC.md §2.3): the chosen status, the price it implies,
// the payment that may come with it, and the sentence explaining what saving
// will actually do.

const EMPTY_NOTES = { student: '', parent: '', private: '' };

// The cancellation-notice ticks, closed. The pair, the toggle and the rest of
// the notice's decisions are src/domain/lessonAttendance.js; who "students"
// means and what counts as an address are shared with RecurringScopeDialog in
// domain/notifications.js.
const emptyNotices = () => ({
  [CHANNELS.email]: { ...CLOSED_NOTICE },
  [CHANNELS.sms]: { ...CLOSED_NOTICE },
});

export default function useAttendanceForm({ occurrence, student, onSaved }) {
  const { statusFor, recordFor, setAttendance } = useAttendance();
  // Whether a text can be sent at all (#782); off until the server says so.
  const smsAvailable = useSmsAvailable();
  const { remainingFor } = usePackages();
  const { summaryFor: makeupFor } = useMakeup();
  // The event's category, for #351's gate. `byId` returns null for an id that
  // names no category — including an occurrence carrying only a bare
  // `categoryLabel` (#110) — and `allowsMakeupCredits` reads that as off
  // (domain/categories.js).
  const { byId: categoryById } = useCategories();
  const { entries } = useLedger();
  const { byId: familyById, contactsFor } = useFamilies();
  const { messageBatch, messages } = useNotifications();
  const { user } = useAuth();
  // The studio's currency (src/domain/locale.js). The price and payment boxes
  // are typed decimals and the sentence under them reads the same money back,
  // so both must use it; a mismatch is a hundredfold error in the sentence a
  // teacher reads before charging a family.
  const currency = useCurrency();
  const formatMoney = useMoneyFormat();
  const { decimalSeparator, formatTime } = useLocale();

  // The payment lives on the ledger, not on the attendance record, so it is
  // read back from there. Otherwise the drawer would reopen with the box clear
  // and the next save would retract the payment, adding the money back onto
  // the family's balance.
  const existingPayment = useMemo(() => {
    if (!occurrence || !student) return null;
    const id = coreSync().attendancePaymentId(occurrence.id, student.id);
    // isActive rather than a hand-rolled !voidedAt: a tombstone is two fields
    // and only the domain predicate knows both (#70).
    const found = entries.find((e) => e.id === id && isActive(e));
    return attendancePaymentReadBack(found);
  }, [entries, occurrence?.id, student?.id]);

  const currentStatus = occurrence && student
    ? statusFor(occurrence.id, student.id)
    : coreSync().defaultAttendanceStatus();

  const record = occurrence && student ? recordFor(occurrence.id, student.id) : null;

  const [status, setStatus] = useState(currentStatus);
  const [priceText, setPriceText] = useState('');
  const [paidAtLesson, setPaidAtLesson] = useState(false);
  const [paidText, setPaidText] = useState('');
  const [paymentNote, setPaymentNote] = useState('');
  // Three bodies, one per audience, as in MMS: switching audience changes both
  // the placeholder and the "visible to" line. Keyed by audience id.
  const [notes, setNotes] = useState(EMPTY_NOTES);
  const [noteAudience, setNoteAudience] = useState('student');
  // The Linked Online Resources (#424): `files` row ids, in the order linked.
  // Saved with the notes, on the same record — a link is per student per
  // lesson, as a note is.
  const [resourceIds, setResourceIds] = useState([]);
  // Who to email. MMS offers a single receipt recipient (a select defaulting to
  // "No Email Receipt", listing the family's contacts) plus a "CC me", and for
  // the notes a checkbox per contact plus "Myself" (#413). Addresses rather
  // than booleans, because a contact may have no email at all.
  const [receiptTo, setReceiptTo] = useState('');
  const [receiptCcMe, setReceiptCcMe] = useState(false);
  const [notesTo, setNotesTo] = useState([]);
  // Who to tell that the lesson is off (#187). Same shape as the delete
  // dialog's — per channel, whether the channel is on and which audiences —
  // because it feeds the same `cancellationRecipients`. Off by default (see
  // domain/notifications.js).
  const [notices, setNotices] = useState(emptyNotices);
  const [cancellationNote, setCancellationNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // Which send button's save-then-send is running (#732), and, per button,
  // what the drawer held when its message was last queued from here — so the
  // button reads Sent until something it sends changes (`sendKey` below).
  const [sendingKind, setSendingKind] = useState(null);
  const [sentKeys, setSentKeys] = useState({});

  useEffect(() => { setStatus(currentStatus); }, [currentStatus, occurrence?.id, student?.id]);

  // Load whatever was saved before. Keyed on the record's own identity so
  // reopening the drawer on another lesson does not show the last one's notes.
  useEffect(() => {
    setNotes({
      student: record?.notes?.student || '',
      parent: record?.notes?.parent || '',
      private: record?.notes?.private || '',
    });
  }, [record?.occurrenceId, record?.studentId, record?.notes]);

  // The same, for the links. A record written before #424 has none.
  useEffect(() => {
    setResourceIds(Array.isArray(record?.resourceIds) ? record.resourceIds.filter((id) => typeof id === 'string') : []);
  }, [record?.occurrenceId, record?.studentId, record?.resourceIds]);

  useEffect(() => {
    setPaidAtLesson(Boolean(existingPayment));
    if (existingPayment) {
      setPaidText(coreSync().toAmountField(existingPayment.amountMinor, currency));
      // 'Paid at lesson' is the placeholder this writes when no note was
      // given; the read-back has already emptied it, so it is not shown as if
      // the teacher typed it.
      setPaymentNote(existingPayment.note);
    }
  }, [existingPayment?.id, existingPayment?.amountMinor, existingPayment?.note]);

  // The people this lesson's messages can go to: every one of the family's
  // contacts (#413), and the teacher themselves for a copy.
  const family = useMemo(
    () => (student ? familyById(student.familyId) : null),
    [familyById, student?.familyId],
  );
  // The `familyContacts` records the account's post goes to: every contact
  // (#413), as MMS addresses all of them and BCCs. `contactsFor` answers from
  // the provider's per-family cache.
  const familyContactList = useMemo(
    () => (student?.familyId ? contactsFor(student.familyId) : []),
    [contactsFor, student?.familyId],
  );
  const contacts = useMemo(
    () => familyRecipients(family, familyContactList),
    [family, familyContactList],
  );
  const me = useMemo(
    () => myselfRecipient(user?.email || null),
    [user?.email],
  );
  // The lesson notes' checkboxes: the contacts, then Myself unless the
  // teacher's address is already one of theirs.
  const notesList = useMemo(
    () => notesRecipients(family, familyContactList, user?.email || null),
    [family, familyContactList, user?.email],
  );

  // Reopening the drawer on another lesson must not carry the last one's
  // recipients: a receipt addressed by accident is a message to a family.
  useEffect(() => {
    setReceiptTo('');
    setReceiptCcMe(false);
    setNotesTo([]);
    // And the cancellation notice, message box included.
    setNotices(emptyNotices());
    setCancellationNote('');
    setSentKeys({});
  }, [occurrence?.id, student?.id]);

  // ─── Telling the family the lesson is off (#187) ──────────────────────────

  // Whether this save calls the lesson off at all. Both Teacher Cancelled
  // statuses count — the one that issues a make-up credit is still a lesson the
  // teacher called off, and the family still needs telling.
  const { cancelling } = attendanceDisclosure({ status });

  // One group, one student: `cancellationRecipients`' shape, built for a single
  // attendance row. The other students on a group lesson have their own rows
  // and their own decision.
  const noticeGroups = useMemo(
    () => (student
      ? [{
        familyId: student.familyId || null, family, contacts: familyContactList, students: [student],
      }]
      : []),
    [student, family, familyContactList],
  );

  // Has a reminder for this lesson already reached this family?
  //
  // Rebuilt from `eventReminderSubject` rather than a second copy of the
  // template, so it cannot drift from what the worker wrote; a drift would
  // answer "no reminder has gone out" for every lesson.
  //
  // `sending` counts with `sent`: a message the sender has claimed has passed
  // #156's gate in functions/delivery/facts.js and is on its way. A `queued`
  // reminder is not counted: that gate refuses it once this save marks the
  // lesson cancelled.
  const remindedChannels = useMemo(
    () => channelsReminded(messages, occurrence, student),
    [messages, occurrence?.id, occurrence?.date, student?.familyId],
  );

  const reminderWarning = reminderAlreadySentText(remindedChannels);

  // A notice already asked for on this lesson-and-student, on any channel to
  // any audience. `queue` would drop a repeat, so the ticks are not offered
  // again.
  const noticeAlreadyQueued = useMemo(
    () => alreadyQueued(messages, occurrence, student),
    [messages, occurrence?.id, student?.id, student?.familyId],
  );

  // The picker is offered only where it can do something: the lesson is being
  // called off, and nobody has asked for this notice yet.
  const showCancellationNotices = cancelling && !noticeAlreadyQueued;

  const setNoticeChannel = (channel, next) => setNotices((n) => (
    { ...n, [channel]: { ...n[channel], ...next } }
  ));

  // Anything selected collapses and clears; nothing selected opens with both
  // audiences ticked, as the delete dialog does.
  //
  // The SMS channel does not open while texts cannot be sent (#782,
  // domain/smsGate.js): the box is disabled, and this is the second lock.
  const toggleNoticeChannel = (channel) => {
    if (channel === CHANNELS.sms && !smsAvailable) return;
    setNoticeChannel(channel, toggledNoticeChannel(notices[channel]));
  };

  // What the queue is asked for: per channel, the ticked audiences or null.
  // A channel open with both audiences unticked sends nothing, and says so by
  // being null rather than by being an empty send. The SMS half is dropped
  // while texts cannot be sent, whatever the form still holds — the gate can
  // close under an open drawer when the connection goes (#782).
  const chosenNotices = () => smsGatedChoice(noticesChosen(notices, cancellationNote), smsAvailable);

  // What this lesson costs before any manual override: a fixed price on the
  // occurrence wins, then the student's own pricing mode.
  const computedMinor = useMemo(() => {
    // The fourth argument is the all-day flag (#322): `allDay` blanks the
    // Duration box and leaves `durationMin` behind it, so an `hourly` student
    // on a day-long event has no duration to price from and the core refuses.
    // The same flag goes to `useAttendance`'s ledger write and the Day view's
    // tile, so the preview and the charge agree (#160).
    //
    // `?? 0` opens the price box at zero for a refused price, as for a
    // fixed-monthly student. It reaches `setAttendance` as
    // `priceOverrideMinor: 0`, and the write bills only `computed > 0`, so a
    // zero writes no charge; a teacher who wants an all-day event billed types
    // a figure over it.
    return attendanceComputedMinor(occurrence, student);
  }, [occurrence, student]);

  useEffect(() => { setPriceText(coreSync().toAmountField(computedMinor, currency)); }, [computedMinor, currency]);

  // The payment defaults to the full lesson price, as in MMS, and follows the
  // price until the box is ticked; after that it is the teacher's number.
  useEffect(() => {
    if (!paidAtLesson && !existingPayment) setPaidText(priceText);
  }, [priceText, paidAtLesson, existingPayment]);

  const meta = coreSync().resolveAttendanceStatus(status);

  // ── The statuses this student may be given (#303, #351, #317) ─────────────
  //
  // Three MMS gates, all in `attendanceStatusesFor` in the core
  // (`core/src/attendance.rs` has the matrix and the argument for each). This
  // hook does no filtering of its own.
  //
  //   1. The balance. MMS adds three `Use Make-Up Credit` rows when the
  //      student has credits and offers eight rows when they do not.
  //      `available`, not `total`: a credit booked against a future make-up
  //      lesson is committed, and offering it again lets one credit pay twice.
  //   2. The event's category. With MMS's `Allow make-up credits to be used for
  //      this event type` off there are six rows at any balance, the two that
  //      issue a credit going with the three that spend one.
  //   3. The event itself. `This event requires a make-up credit`, read only
  //      when 2 is on, withdraws `Present` and `Present, Late` when there is a
  //      credit to require.
  //
  // Both flags default off when they cannot be read: `allowsMakeupCredits` is
  // false for a `categoryId` naming no category (#110), and an event written
  // before #317 has no `requiresMakeupCredit`.
  //
  // The balance is a quotient (#352), the gate is not (#406): a credit is a
  // lesson-length of time, so five 30-minute credits read 3.33 at a 45-minute
  // lesson length. What crosses is `availableMin`, because MMS's gate is
  // whether the balance covers this lesson — `availableMin >=
  // occurrence.durationMin` (`statuses_for`) — and the rounded quotient would
  // decide a boundary case by a rounding.
  //
  // `availableMin` is `0` with nothing loaded (`?? 0`), so the spending rows
  // are hidden rather than offered against an unknown balance.
  //
  // `occurrenceMin` is the occurrence's own stored duration, not the student's
  // lesson length: it is this lesson the credit pays for. `|| 0` on a missing
  // or all-day duration reaches the core's `occurrence_minutes > 0` half,
  // which refuses the spending rows.
  //
  // `except` is this occurrence (#363): a flagged occurrence books its credit
  // forward, and without it the drawer would be told the credit it is about to
  // spend is already spoken for. MMS shows the spending rows for a flagged
  // event with one credit in hand. See `summaryFor` in data/useMakeup.jsx.
  const makeupSummary = student
    ? makeupFor(student.id, student.durationMin, { except: occurrence?.id ?? null })
    : null;
  const availableMin = makeupSummary?.availableMin ?? 0;
  const occurrenceMin = Number(occurrence?.durationMin) || 0;
  const category = occurrence ? categoryById(occurrence.categoryId) : null;
  const allowsCredits = allowsMakeupCredits(category);
  const requiresCredit = occurrence?.requiresMakeupCredit === true;
  const statuses = useMemo(
    () => coreSync().attendanceStatusesFor(
      availableMin, occurrenceMin, allowsCredits, requiresCredit, status,
    ),
    [availableMin, occurrenceMin, allowsCredits, requiresCredit, status],
  );
  // MMS shows no price field until a status has been chosen, even though
  // Unrecorded is billable, so the disclosure keys on the decision.
  //
  // It also decides what a save applies: the payment box keeps its state while
  // hidden, so a teacher who ticks "paid at lesson" and sets the status back to
  // Unrecorded would otherwise record a live payment against a voided charge.
  // A control the form is not showing contributes nothing.
  //
  // Whether this lesson bills is the status and the occurrence's `This event
  // requires a make-up credit` together (#362), read by both the disclosure and
  // the consequence sentence so the two cannot disagree.
  const { showPrice, paymentApplies } = attendanceDisclosure({
    status, requiresMakeupCredit: requiresCredit, paidAtLesson,
  });

  // The Lesson Price box's own field message (#171). `optionalMinor` in
  // firestore.rules carries MAX_MONEY_MINOR (#164); past it,
  // `useAttendance.setAttendance`'s single `writeBatch` — the charge, the
  // credit, the package consume and the payment together — would be refused
  // whole as a bare permission-denied.
  //
  // Judged only while the field is shown: hidden, `priceText` still tracks the
  // computed price, but `chargeApplies` in `useAttendance.jsx` never lets an
  // unbillable status reach `amountMinor`.
  const { price: priceErrorText, paid: paidErrorText } = attendanceFieldErrors({
    priceText, paidText, showPrice, paymentApplies,
  }, currency);
  const remaining = student ? remainingFor(student.id) : 0;
  const usesPackage = attendanceUsesPackage(remaining, occurrence?.pricingRule);

  // The Paid-at-Lesson box's own field message (#174), for the `payment`-kind
  // `ledgerEntries` write. `validLedgerEntry` bounds `amountMinor` at
  // MAX_MONEY_MINOR, and a figure past it would sink the whole attendance
  // batch. A negative cannot reach the batch: `setAttendance` clamps with
  // `Math.max(0, …)`.
  //
  // Judged only while a payment applies, which is while the field is on screen
  // (AttendanceTab renders "Paid at Lesson" inside `paidAtLesson`, inside
  // `showPrice`); `save()` reads `paidText` only when `paymentApplies`.
  // (`paidErrorText` is read with `priceErrorText` above.)

  // Said in full, because this is the moment the money is decided and the
  // teacher cannot see the invoice from here.
  //
  // The sentence is `attendanceConsequence` in src/domain/lessonAttendance.js:
  // a spend names the credits it leaves, in minutes (#303, #352); `lessonBills`
  // rather than the status's billing (#362); prepaid time in minutes (#181,
  // `packageDrawSentence`); and a payment spelled out. What stays here is the
  // studio's locale writing the three figures.
  const consequence = useMemo(() => {
    const { chargeMinor, paidMinor, owingMinor, differenceMinor } = attendanceSettlement(
      { priceText, paidText, computedMinor }, currency,
    );
    return attendanceConsequence({
      status,
      savedStatus: record?.status,
      requiresMakeupCredit: requiresCredit,
      usesPackage,
      remaining,
      availableMin: makeupSummary?.availableMin ?? 0,
      lessonDurationMin: student?.durationMin,
      occurrenceDurationMin: occurrence?.durationMin,
      paymentApplies,
      owingMinor,
    }, {
      charge: formatMoney(chargeMinor),
      paid: formatMoney(paidMinor),
      difference: formatMoney(differenceMinor),
      decimal: decimalSeparator,
    });
  }, [status, requiresCredit, usesPackage, remaining, makeupSummary, student, occurrence,
    record, priceText, computedMinor,
    paymentApplies, paidText, currency, formatMoney, decimalSeparator]);

  // ── The three messages, each sent from its own button (#732) ────────────
  //
  // A knowing difference from MMS, whose drawer sends the receipt, the lesson
  // notes and the cancellation notices when Save is pressed although nothing
  // on screen says so. Here Save saves and queues nothing, and each message
  // has a button beside its controls — Email Lesson Notes, Email Receipt, Send
  // Cancellation Notice. The rule for each is `attendance_send_state` in
  // core/src/messages.rs, which the phones ask too.
  //
  // A send button saves first, then sends: Save's own write with its one
  // message as the follow-on, so what was emailed is what is on record. After
  // the attendance batch rather than inside it, because a receipt for a
  // payment that failed to save is a receipt for nothing, and a notice for a
  // mark that failed would tell a family a lesson is off while the calendar
  // says it is on. (The delete path queues first and deletes after: there the
  // delete is what the family is told about, and it cannot be undone.)
  //
  // "After" is the write layer's to keep (#590): the messages are built here,
  // uncommitted, and handed to `setAttendance` as its follow-on — written onto
  // the attendance batch's durable note at the click, and queued when that
  // batch is acknowledged, in whichever page that happens. A refused
  // attendance sends nothing. A message answered after the drawer has closed
  // shows on the sync chip.
  //
  // `showCancellationNotices` and not `cancelling`: a notice already asked
  // for is not asked for twice.
  const messageOptions = (kind) => ({
    occurrence,
    student,
    family,
    contacts: familyContactList,
    myEmail: user?.email || null,
    receiptTo,
    receiptCcMe,
    notesTo,
    paymentApplies,
    showCancellationNotices,
    chosen: chosenNotices(),
    only: ATTENDANCE_SENDS[kind],
  });

  const { paidMinor } = attendanceSettlement({ priceText, paidText, computedMinor }, currency);

  // What each button sends, as a key: while it matches what was queued from
  // this drawer the button reads Sent, and changing anything it sends offers
  // it again. Trimmed notes and minor units rather than the boxes' text,
  // because the save's read-back rewrites both boxes (`30` comes back
  // `30.00`).
  const sendKey = (kind) => JSON.stringify(
    kind === 'notes' ? [[...notesTo].sort(), notes.student.trim(), notes.parent.trim()]
      : kind === 'receipt' ? [receiptTo, receiptCcMe, paidMinor]
        : [status, notices, cancellationNote],
  );

  const wording = attendanceSendWording();
  const sendFor = (kind) => {
    const state = attendanceSendState(messageOptions(kind), messages, { notes, paidMinor });
    const justSent = sentKeys[kind] === sendKey(kind);
    const sent = justSent || state === ATTENDANCE_SEND_STATES.sent;
    return {
      label: sent ? wording.sent : wording[kind],
      // Offered only where pressing it would queue something.
      enabled: !sent && state === ATTENDANCE_SEND_STATES.ready && !saving,
      sending: sendingKind === kind,
      sent,
      // Said under the button: that this press went, or that an earlier one
      // already did.
      line: justSent ? wording.sentLine : sent ? wording.alreadySent : null,
    };
  };

  // Save's write, with what is to follow it: nothing for Save itself, one
  // message for a send button. True once the attendance is saved.
  const commit = async (followOn) => {
    // Both fields already carry their sentences (#171, #174) and Save is never
    // disabled here, as #115 settled for the expense form (`entryErrors` in
    // useExpenseEntryForm.js). One guard for the two, because the charge, the
    // credit, the package consume and the payment share one batch.
    if (priceErrorText || paidErrorText) return false;
    setSaving(true);
    setSaveError(null);
    try {
      await setAttendance({
        occurrence: {
          ...occurrence,
          // The start on the studio's clock, `4:00 PM (30 min.) - Lesson`
          // in the United States (#618).
          description: attendanceDescription(occurrence, occurrence?.startTime ? formatTime(occurrence.startTime) : null),
        },
        student: {
          id: student.id,
          familyId: student.familyId,
          pricingMode: student.pricingMode,
          rateMinor: student.rateMinor,
          // No `durationMin` (#374, #365): `useEventForm.js` keeps
          // `durationMin` while All Day is ticked and `firestore.rules` admits
          // only 1..1440, so a credit always has an occurrence duration. A
          // credit the writer cannot measure is worth nothing, visibly
          // (`entryMinutes` in src/domain/makeup.js).
        },
        status,
        priceOverrideMinor: priceText !== '' ? coreSync().fromDecimal(priceText, currency) : null,
        packageRemaining: remaining,
        notes,
        resourceIds,
        payment: {
          paid: paymentApplies,
          amountMinor: paidText !== '' ? coreSync().fromDecimal(paidText, currency) : 0,
          note: paymentNote,
        },
        followOn,
      });
      return true;
    } catch (err) {
      // A rejected write must be visible: this save produces a charge, and a
      // drawer looking idle after a failure is how a lesson ends up taught but
      // never billed.
      setSaveError(err?.message || String(err));
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Save: the attendance, the notes, the payment and the links — and no
  // message, whatever is ticked (#732).
  const save = async () => {
    if (await commit(null)) onSaved?.();
  };

  // A send button: Save's write, then its one message. Refused where the
  // button is not offered, so a stray call cannot queue what the screen said
  // it would not.
  const send = async (kind) => {
    if (!sendFor(kind).enabled) return;
    const key = sendKey(kind);
    const requests = attendanceMessageRequests(messageOptions(kind));
    const followOn = requests.length ? messageBatch(requests)?.batch ?? null : null;
    setSendingKind(kind);
    const ok = await commit(followOn);
    setSendingKind(null);
    if (ok) setSentKeys((k) => ({ ...k, [kind]: key }));
  };

  return {
    status, setStatus, meta, consequence,
    // The rows the `<select>` draws, already gated (#303). The tab renders
    // what it is handed.
    statuses,
    showPrice,
    priceText, setPriceText, priceError: priceErrorText,
    paidAtLesson, setPaidAtLesson,
    paidText, setPaidText, paidError: paidErrorText,
    paymentNote, setPaymentNote,
    contacts, me, notesRecipients: notesList,
    receiptTo, setReceiptTo,
    receiptCcMe, setReceiptCcMe,
    // Calling the lesson off (#187): whether to offer the notice, what the
    // family has already been told, and the ticks themselves.
    showCancellationNotices,
    noticeAlreadyQueued: cancelling && noticeAlreadyQueued,
    reminderWarning,
    notices,
    noticeGroups,
    setNoticeAudience: setNoticeChannel,
    toggleNoticeChannel,
    cancellationNote, setCancellationNote,
    notesTo,
    toggleNotesTo: (address) => setNotesTo((list) => (
      list.includes(address) ? list.filter((a) => a !== address) : [...list, address]
    )),
    noteAudience, setNoteAudience,
    notes,
    // Clamped, not just bounded by the textarea's maxLength: `Templates`
    // appends a template body to the box (domain/noteTemplates applyTemplate),
    // and a value set programmatically never meets maxLength, so two templates
    // could carry a note past what firestore.rules accepts and the whole
    // attendance batch would be refused. Truncating is the recoverable
    // direction, and the counter under the box says the note is full (#93).
    // `clampNote` reads NOTE_BODY_MAX, so it moves with the bound.
    setNote: (audience, value) => setNotes((n) => (
      { ...n, [audience]: clampNote(value) }
    )),
    // Linked Online Resources (#424). Linking is the picker's Link Selected
    // (`link_selection`, which keeps what was there, adds what was picked
    // once each and stops at fifty) and unlinking one is `unlink`; both only
    // change the form, and Save writes them.
    resourceIds,
    linkResources: (selected) => setResourceIds((ids) => linkSelection(ids, selected)),
    unlinkResource: (id) => setResourceIds((ids) => unlink(ids, id)),
    saving, saveError, save,
    // The three send buttons (#732): each one's label, whether it is
    // offered, whether its save is running, and the line under it.
    sends: { notes: sendFor('notes'), receipt: sendFor('receipt'), notice: sendFor('notice') },
    send, sendingKind,
    noticeQueuedText: wording.noticeQueued,
    record,
  };
}
